import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import { useJobCreate } from "../../../context/JobCreateContext";
import {
  validateJobCraftDetails,
  isEmptyValue,
} from "../../../util_functions/helpers";
const JobSupportDetailsComponent = ({
  onUpdate,
  isJobCraftError,
  clearError,
}) => {
  const navigate = useNavigate();

  const { fetchJobSelectionsById, getJobCreateIdFromCookies } = useJobCreate();

  const defaultJobCraftDetails = {
    jobTitle: "",
    jobDescription: "",
    preferredQualities: "",
    personalDetails: "",
  };

  const [craftJobDetailsData, setCraftJobDetailsData] = useState(
    defaultJobCraftDetails
  );

  const [computedErrors, setComputedErrors] = useState([]);
  const [visibleErrors, setVisibleErrors] = useState([]);

  const hasErrorForField = (fieldName) =>
    visibleErrors.some((e) => e.field === fieldName);


  const jobTitleInputRef = useRef(null);
  const jobCraftDetailsRef = useRef(null);

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  const formats = [
    "font", "size", "bold", "italic", "underline", "strike",
    "list", "bullet", "link", "image", "color",
  ];

  useEffect(() => {
    if (isJobCraftError) {
      const fallback = validateJobCraftDetails(craftJobDetailsData);
      setVisibleErrors(computedErrors.length ? computedErrors : fallback);
      smoothScrollToTop();
    } else {
      setVisibleErrors([]);
    }
  }, [isJobCraftError]);


  useEffect(() => {
    const jobCreateId = getJobCreateIdFromCookies();
    if (!jobCreateId) {
      navigate("/account/fs-dashboard/my-jobs/new/standard");
    } else {
      fetchJobSelectionsById(jobCreateId)
        .then((fetchedSelections) => {
          const jobCraftDetails =
            fetchedSelections?.jobCraftDetails || defaultJobCraftDetails;
          setCraftJobDetailsData(jobCraftDetails);
        })
        .catch((error) => {
          console.error("Error fetching job selections:", error);
        });
    }
  }, [getJobCreateIdFromCookies, navigate, fetchJobSelectionsById]);

  useEffect(() => {
    if (jobTitleInputRef.current) {
      jobTitleInputRef.current.focus();
    }
  }, []);

  const smoothScrollToTop = () => {
    const container = jobCraftDetailsRef.current;
    if (container) {
      const scrollDuration = 600;
      const scrollStep = container.scrollTop / (scrollDuration / 15);

      const scrollInterval = setInterval(() => {
        if (container.scrollTop > 0) {
          container.scrollTop -= scrollStep;
        } else {
          clearInterval(scrollInterval);
        }
      }, 15);
    }
  };

  const notifyUpdate = (changes) => {
    const updated = { ...craftJobDetailsData, ...changes };
    const errors = validateJobCraftDetails(updated);

    setComputedErrors(errors);
    setCraftJobDetailsData(updated);
    onUpdate({ data: updated, errors, isValid: errors.length === 0 });

    if (isJobCraftError) setVisibleErrors(errors);
    else setVisibleErrors([]);
  };


  const handleEditorChange = (value, name) => {
    clearError();
    notifyUpdate({ [name]: value });
  };

  return (
    <div className="support-details-component">
      <div className="support-details-scrollable" ref={jobCraftDetailsRef}>
        {isJobCraftError && visibleErrors.length > 0 && (
          <div className="fdtc-error-message">
            <ul>{visibleErrors.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
          </div>
        )}

        <div
          className={`job-title ${hasErrorForField("jobTitle") ? "error-highlight" : ""
            }`}
        >
          <h4>Enter the Job Title</h4>
          <input
            type="text"
            name="jobTitle"
            ref={jobTitleInputRef}
            value={craftJobDetailsData.jobTitle}
            onChange={(e) => handleEditorChange(e.target.value, e.target.name)}
          />
          <div className="job-description-note">
             Summarize your position title. <strong>e.g :</strong> Assist client with personal care for toilet, meal preparation, shopping and community access...
          </div>
        </div>
        <div
          className={`job-description ${hasErrorForField("jobDescription") ? "error-highlight" : ""
            }`}
        >
          <h4>How Would You Describe the Role?</h4>
          <ReactQuill
            theme="snow"
            value={craftJobDetailsData.jobDescription}
            onChange={(content) =>
              handleEditorChange(content, "jobDescription")
            }
            modules={modules}
            className="react-quill-custom"
          />
          <span>
            {(craftJobDetailsData.jobDescription || "").length} / 500
          </span>
        </div>
        <div className="job-preferred-qualities" >
          <h4>
            Preferred qualities (Optional)
          </h4>
          <p>
              May include personality, skills, experience, age, language, or other preferences.
            </p>
          <ReactQuill
            theme="snow"
            value={craftJobDetailsData.preferredQualities}
            onChange={(content) =>
              handleEditorChange(content, "preferredQualities")
            }
            modules={modules}
            formats={formats}
            className="react-quill-custom"
          />
          <span>
            {(craftJobDetailsData.preferredQualities || "").length} / 500
          </span>
        </div>
        <div
          className={`job-additional-details ${hasErrorForField("personalDetails") ? "error-highlight" : ""
            }`}
        >
          <h4>
            Are there any other important details the applicant should consider?
          </h4>
          <ReactQuill
            theme="snow"
            value={craftJobDetailsData.personalDetails}
            onChange={(content) =>
              handleEditorChange(content, "personalDetails")
            }
            modules={modules}
            formats={formats}
            className="react-quill-custom"
          />
          <span>
            {(craftJobDetailsData.personalDetails || "").length} / 500
          </span>
        </div>
      </div>
    </div>
  );
};

export default JobSupportDetailsComponent;
